import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import Button from '@appchoose/button';
import { Loader2 } from 'lucide-react';

export interface DescriptionEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialValue: string;
  /** Quand false, le texte n’est pas encore fiable : affichage d’un loader à la place du champ. */
  descriptionsReady: boolean;
  onSave: (value: string | null) => Promise<boolean>;
  disabled?: boolean;
}

export function DescriptionEditDialog({
  open,
  onOpenChange,
  initialValue,
  descriptionsReady,
  onSave,
  disabled = false,
}: DescriptionEditDialogProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft(initialValue);
      setSaving(false);
    }
  }, [open, initialValue]);

  const handleSave = async () => {
    const trimmed = draft.trim();
    const value = trimmed === '' ? null : trimmed;
    setSaving(true);
    try {
      const ok = await onSave(value);
      if (ok) onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  const isBusy = saving || !descriptionsReady;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && saving) return;
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('kpi.description_modal_title')}</DialogTitle>
          <DialogDescription>
            {descriptionsReady
              ? t('kpi.description_modal_subtitle')
              : t('kpi.description_modal_loading')}
          </DialogDescription>
        </DialogHeader>
        {descriptionsReady ? (
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t('kpi.description_modal_placeholder')}
            disabled={disabled || saving}
            rows={10}
            className="resize-y"
          />
        ) : (
          <div
            className="flex min-h-[220px] w-full flex-col items-center justify-center gap-3 rounded-md border border-input bg-muted/30"
            role="status"
            aria-live="polite"
            aria-busy
          >
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">
              {t('kpi.description_modal_loading')}
            </p>
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            appearance="outline"
            onClick={() => onOpenChange(false)}
            disabled={disabled || saving}
          >
            {t('general.cancel')}
          </Button>
          <Button
            type="button"
            appearance="primary"
            onClick={() => void handleSave()}
            disabled={disabled || isBusy}
          >
            {t('general.validate')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
